import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { fmtDateET, fmtMoney, statusBadge, statusLabel } from './paymentUtils';
import { QboRefs } from './QboRefs';

const PreviewTable = ({ results }) => (
  <div className="overflow-x-auto border rounded-lg">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 sticky top-0">
        <tr className="text-left text-xs text-slate-500">
          <th className="px-3 py-2">Date</th><th className="px-3 py-2">Payee</th>
          <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Result</th>
          <th className="px-3 py-2">QBO</th><th className="px-3 py-2">Note</th>
        </tr>
      </thead>
      <tbody>
        {results.length === 0 && (
          <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500" data-testid="mercury-preview-empty">No outgoing Mercury payments in the last 60 days.</td></tr>
        )}
        {results.map((r) => (
          <tr key={r.mercury_txn_id} className="border-t" data-testid={`preview-row-${r.mercury_txn_id}`}>
            <td className="px-3 py-2 whitespace-nowrap">{fmtDateET(r.date)}</td>
            <td className="px-3 py-2">{r.counterparty_name}</td>
            <td className="px-3 py-2 text-right">{fmtMoney(r.amount)}</td>
            <td className="px-3 py-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(r.new_status)}`}>
                {r.new_status}{r.would_post ? ' (would post)' : ''}
              </span>
            </td>
            <td className="px-3 py-2"><QboRefs payment={{ ...r, mercury_txn_id: `preview-${r.mercury_txn_id}` }} /></td>
            <td className="px-3 py-2 text-xs text-slate-500" title={r.memo}>{r.reason}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const PreviewDialog = ({ open, onClose, apiFetch, apiUrl }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setData(null);
    setError('');
    apiFetch(`${apiUrl}/api/billpay/payments/preview?days=60`)
      .then(async (resp) => {
        const body = await resp.json();
        if (resp.ok) setData(body);
        else setError(body.detail || 'Preview failed');
      })
      .catch(() => setError('Preview failed'));
  }, [open, apiFetch, apiUrl]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="mercury-preview-dialog" className="max-w-5xl w-[95vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Preview last 60 days</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Read-only: nothing is saved or posted.</p>
        {!data && !error && <p className="text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Checking Mercury and QuickBooks…</p>}
        {error && <p className="text-sm text-red-600" data-testid="mercury-preview-error">{error}</p>}
        {data && (
          <>
            {data.warnings?.length > 0 && (
              <p className="text-sm text-amber-700" data-testid="mercury-preview-warnings">{data.warnings.join('. ')}.</p>
            )}
            <p data-testid="mercury-preview-summary" className="text-sm text-slate-700">
              {[...Object.entries(data.summary || {}).map(([k, v]) => `${statusLabel(k)}: ${v}`), ''].join(' · ')}
              <strong data-testid="mercury-preview-would-post">would post: {data.would_post}</strong>
            </p>
            <PreviewTable results={data.results} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
